import { PageHeader } from '@keplerco/core';
import React from 'react';
import { PagePath } from '../../../../navigation/navigation.enums';
import { useParams } from 'react-router';
import { useAppActions, useAppState } from '../../../../overmind';
import { Learner } from '../../../../models';
import { PageSkeletonLoader } from '../../../../components/general/loading-state/loader-layers/page-loader-layer/page-skeleton-loader';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { SunburstWidget } from '../../../../widgets/analytics/sunburst/sunburst.widget';
import { AssessmentSearchParams, PermissionSearchParams } from '../../../../models/overmind/search-params';
import { OrganizationLevelType } from '../../../../enums';
import { ScoreOverviewWidget } from '../../../../widgets/analytics/score-overview/score-overview.widget';
import { AssessmentSubType, AssessmentType } from '../../../../enums/assessment-type';
import AssessmentScoreSkillWidget from '../../../../widgets/analytics/assessment-score-skill/assessment-score-skill.widget';
import analyticsStyles from '../../analytics.module.css';
import { YearsExperienceWidget } from '../versus-score/years-experience.widget';
import { FrequencyUseWidget } from '../versus-score/frequency-use.widget';
import AssessmentRatingsWidget from '../../../../widgets/analytics/assessment-ratings/assessment-ratings.widget';
import { ScoreComparison } from '../../../../enums/score-comparison';
import { getSeniorityDescription } from '../../../../library/helpers/get-seniority-description';
import { AssessmentResponse } from '../../../../overmind/actions';
import { OverallFeedbackWidget } from '../../../../widgets/analytics/feedback/overall-feedback.widget';
import { SkillSpecificFeedbackWidget } from '../../../../widgets/analytics/feedback/skill-specific-feedback.widget';
import { AnalyticsFiltersOptions } from './assessment.models';

export function AssessmentPersonPage(): React.ReactElement {
  const { assessmentSlug, personSlug } = useParams();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [assessment, setAssessment] = React.useState<AssessmentResponse>();
  const [person, setPerson] = React.useState<Learner>();
  const [pathways, setPathways] = React.useState<{ name: string; seniority: string }[]>([]);
  const [filtersOptions, setFiltersOptions] = React.useState<AnalyticsFiltersOptions>({
    departmentOptions: [],
    teamOptions: [],
    roleOptions: [],
    skillOptions: [],
    countryOptions: [],
    subjectsOptions: [],
    assessorsOptions: [],
  });
  const [isAnonymous, setIsAnonymous] = React.useState<boolean>(false);

  const permissionSearchParams: PermissionSearchParams | undefined = React.useMemo(() => {
    if (!companyVariables.slug || !person?.slug) return undefined;

    return {
      organizationLevel: OrganizationLevelType.Learner,
      companySlug: companyVariables.slug,
      departmentSlug: undefined,
      teamSlug: undefined,
      learnerSlug: person.slug,
    };
  }, [companyVariables.slug, person?.slug]);

  const assessmentSearchParams: AssessmentSearchParams | undefined = React.useMemo(() => {
    if (!assessment || !companyVariables.slug) return undefined;

    return {
      assessmentType: assessment.assessmentType,
      assessmentSlug: assessment.slug,
      assessmentSubType: assessment.assessmentSubType,
    };
  }, [assessment]);

  React.useEffect(() => {
    async function getData() {
      setIsLoading(true);

      const [assessmentResponse, personData] = await Promise.all([actions.getAssessment(assessmentSlug!), actions.getLearnerBySlug(personSlug!)]);

      setAssessment(assessmentResponse);
      setPerson(personData);

      if (assessmentResponse?.assessmentType === AssessmentType.FieldsOfPractice) {
        const pathwaysResponse = await actions.getLearnerCareerPaths({
          assessmentSlug: assessmentSlug!,
          companySlug: companyVariables.slug!,
          learnerSlug: personSlug!,
        });

        setPathways(
          pathwaysResponse?.map(pathway => ({
            name: pathway.careerPathName,
            seniority: getSeniorityDescription(pathway.seniorityLevelEnum),
          })) || []
        );
      }

      if (assessment?.assessmentSubType === AssessmentSubType.RatingAssessment) {
        const assessors = new Map<string, { name: string; slug: string }>();

        const peerResponse = await actions.getPeerAssessment({ assessmentSlug: assessmentSlug! });
        setIsAnonymous(!!peerResponse?.isAnonymous);
        if (!peerResponse?.isAnonymous) {
          peerResponse?.assignees.forEach(assignee => {
            assignee.reviewers.forEach(reviewer => {
              assessors.set(reviewer.learnerSlug, { name: reviewer.fullName, slug: reviewer.learnerSlug });
            });
          });
        }

        const skillsResponse = await actions.getAssessmentAnalyticsSkills(assessmentSlug!);

        setFiltersOptions(currentState => ({
          ...currentState,
          assessorsOptions: Array.from(assessors.values()).map(assessor => ({ value: assessor.slug, label: assessor.name })),
          skillOptions: skillsResponse?.map(skill => ({ label: skill.name, value: skill.slug! })) ?? [],
        }));
      }

      setIsLoading(false);
    }

    getData();
  }, [assessmentSlug, personSlug, companyVariables.slug]);

  if (isLoading) return <PageSkeletonLoader />;

  return (
    <div className="wrapper stack" style={{ gap: 24 }}>
      <PageHeader
        breadcrumbs={[
          { name: `Analytics`, url: PagePath.analysisBase },
          { name: `Assessments`, url: `${PagePath.analyticsBase}${PagePath.analyticsAssessments.replace(':companySlug', companyVariables.slug!)}` },
          { name: assessment?.name ?? `Assessment`, url: `${PagePath.analyticsBase}${PagePath.analyticsAssessment.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', assessmentSlug!)}` },
          { name: person?.fullName ?? 'Person', url: location.pathname },
        ]}
        title={person?.fullName ?? 'Person'}
        subtitle={pathways.map(pathway => `${pathway.name}: ${pathway.seniority}`).join(' | ')}
        divider
      />

      {!!permissionSearchParams && !!assessmentSearchParams ? (
        <React.Fragment>
          <ScoreOverviewWidget path={PagePath.analyticsAssessmentPerson} baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} />

          <SunburstWidget chartTitle="Score for each skill included in this assessment:" skillScoreTitle="Score" skillScoreUseRAGColours permissionSearchParams={permissionSearchParams} assessmentSearchParams={assessmentSearchParams} scoreComparison={ScoreComparison.None} />

          <AssessmentScoreSkillWidget baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} />

          {assessmentSearchParams.assessmentType === AssessmentType.PeerEndorsement && <AssessmentRatingsWidget assessmentSlug={assessmentSearchParams.assessmentSlug} learnerSlug={permissionSearchParams.learnerSlug!} />}

          {assessmentSearchParams.assessmentSubType === AssessmentSubType.GroupAssessment && (
            <React.Fragment>
              <div className={analyticsStyles.oneOneGrid}>
                <YearsExperienceWidget
                  title="Years of experience vs average skill score"
                  subtitle="The average score for skills included in this assessment compared to how many years of experience the assessment participant has in the skill."
                  baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }}
                />

                <FrequencyUseWidget title="Frequency of use vs average skill score" subtitle="The average score for skills included in this assessment compared to how often the skill is used." baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} />
              </div>
            </React.Fragment>
          )}

          {assessmentSearchParams.assessmentSubType === AssessmentSubType.RatingAssessment && (
            <React.Fragment>
              <SkillSpecificFeedbackWidget isAnonymous={isAnonymous} baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} filtersOptions={filtersOptions} isPerson />
              <OverallFeedbackWidget isAnonymous={isAnonymous} baseSearchParams={{ ...permissionSearchParams, ...assessmentSearchParams }} filtersOptions={{ ...filtersOptions, skillOptions: [] }} isPerson />
            </React.Fragment>
          )}
        </React.Fragment>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}
